import { useState, useEffect, useCallback, useRef } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Tooltip,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Search as SearchIcon, CheckCircle as CheckCircleIcon, Info as InfoIcon } from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import ModalImportacao from "./ModalImportacao";
import { ConfirmarAlteracaoEstadoDialog } from "./components/ConfirmarAlteracaoEstadoDialog";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import { useAuth } from "shared/hooks/auth";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { debounce } from "lodash";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  listarCodigosBase,
  obterEstado,
  alterarEstado,
  importarLoteAplicabilidades,
  baixarPlanilhaModeloXlsx,
  exportarPlanilhaAplicabilidades,
  listarLinhasSistema,
  obterConfiguracaoConsulta,
  obterUltimaImportacao,
  listarHistoricoImportacoes,
  removerImportacao,
  downloadHistoricoImportacao,
  ICodigoBase,
  IEstadoAplicabilidade,
  IFSelect,
  IHistoricoImportacao,
} from "services/modules/aplicabilidades";
import { getEmpresas } from "services/modules/empresas";
import { IEmpresa } from "shared/interfaces/empresa";

const SITUACOES = [
  { value: 0, label: "Em Edição" },
  { value: 1, label: "Em Homologação" },
  { value: 2, label: "Homologada" },
  { value: 3, label: "Disponível Mercado" },
];

export const Aplicabilidades = () => {
  const { user, havePermission } = useAuth();
  const { openNotification } = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const { empresaId } = useParams<{ empresaId: string }>();
  
  const isConsulta = location.pathname.includes("/consulta");
  const menuLabel = isConsulta ? "Consulta" : "Cadastros";
  const pageTitle = isConsulta ? "CONSULTA DE APLICABILIDADES" : "CADASTRO DE APLICABILIDADES";

  const [empresaSelecionada, setEmpresaSelecionada] = useState<IEmpresa | null>(null);
  const [codigosBase, setCodigosBase] = useState<ICodigoBase[]>([]);
  const [estado, setEstado] = useState<IEstadoAplicabilidade | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filtroBusca, setFiltroBusca] = useState("");
  const [somenteComAplicabilidade, setSomenteComAplicabilidade] = useState(false);
  const [filtroProjetista, setFiltroProjetista] = useState<number | "">("");
  const [filtroLinhaSistema, setFiltroLinhaSistema] = useState<number | "">("");
  const [projetistasOpcoes, setProjetistasOpcoes] = useState<IFSelect[]>([]);
  const [linhasSistemaOpcoes, setLinhasSistemaOpcoes] = useState<IFSelect[]>([]);
  const [referenciasOpcoes, setReferenciasOpcoes] = useState<IFSelect[]>([]);
  const [verDescontinuados, setVerDescontinuados] = useState(false);
  const [modalImportacaoAberto, setModalImportacaoAberto] = useState(false);
  const [ultimaImportacao, setUltimaImportacao] = useState<IHistoricoImportacao | null>(null);
  const [historicoImportacoes, setHistoricoImportacoes] = useState<IHistoricoImportacao[]>([]);

  // FASE 05 - T-09/T-10/T-11: confirmação obrigatória antes de qualquer transição de situação.
  const [confirmacaoEstado, setConfirmacaoEstado] = useState<{
    open: boolean;
    novaSituacao: number | null;
    titulo: string;
    mensagem: string;
    textoBotao: string;
  }>({ open: false, novaSituacao: null, titulo: "", mensagem: "", textoBotao: "" });

  const TEXTOS_CONFIRMACAO: Record<number, { titulo: string; mensagem: string; textoBotao: string }> = {
    1: {
      titulo: "Homologar aplicabilidades",
      mensagem:
        "As aplicabilidades em situação Em Edição passarão para Em Homologação e ficarão disponíveis para homologação no software CEM. Essa ação não poderá ser desfeita. Deseja continuar?",
      textoBotao: "HOMOLOGAR",
    },
    2: {
      titulo: "Concluir homologação",
      mensagem:
        "As aplicabilidades em situação Em Homologação passarão para Homologada e estarão prontas para serem disponibilizadas aos clientes no software CEM. Essa ação não poderá ser desfeita. Deseja continuar?",
      textoBotao: "CONCLUIR HOMOLOGAÇÃO",
    },
    3: {
      titulo: "Disponibilizar para o mercado",
      mensagem:
        "As aplicabilidades em situação Homologada passarão para Disponível Mercado e ficarão disponíveis para uso dos clientes no software CEM. Essa ação não poderá ser desfeita. Deseja continuar?",
      textoBotao: "DISPONIBILIZAR MERCADO",
    },
  };

  const abrirConfirmacaoEstado = (novaSituacao: number) => {
    const t = TEXTOS_CONFIRMACAO[novaSituacao];
    if (!t) return;
    setConfirmacaoEstado({
      open: true,
      novaSituacao,
      titulo: t.titulo,
      mensagem: t.mensagem,
      textoBotao: t.textoBotao,
    });
  };

  const fecharConfirmacaoEstado = () => {
    setConfirmacaoEstado({ open: false, novaSituacao: null, titulo: "", mensagem: "", textoBotao: "" });
  };

  const confirmarAlteracaoEstado = async () => {
    if (confirmacaoEstado.novaSituacao == null) return;
    const situacaoSelecionada = confirmacaoEstado.novaSituacao;
    fecharConfirmacaoEstado();
    await handleAlterarEstado(situacaoSelecionada);
  };

  const podeImportar = (estado?.situacao ?? 0) === 3;
  // FASE 03 (item 3 / 22-06): após importar, a situação sai de "Disponível Mercado" e o
  // IMPORTAR desabilitaria, deixando o usuário sem acesso ao "desfazer importação" (dentro do
  // modal). Mantemos o botão acionável enquanto houver uma importação pendente removível, apenas
  // para alcançar o "desfazer". O re-upload (BUSCAR/IMPORTAR) continua gated em "Disponível
  // Mercado" dentro do modal — re-importar só após desfazer (decisão do PO).
  const ultimaImportacaoPodeRemover = ultimaImportacao?.podeRemover === true;
  const habilitarImportar = podeImportar || ultimaImportacaoPodeRemover;
  const tooltipImportar = "Disponível apenas quando todas as aplicabilidades estiverem em Disponível Mercado.";
  const isMaster = havePermission(["Master"]);

  // Refs para filtros de colunas (mantidos para compatibilidade de foco)
  const refFiltroCodigo = useRef<HTMLInputElement>();
  const refFiltroCor = useRef<HTMLInputElement>();
  const refFiltroDescricao = useRef<HTMLInputElement>();
  const refFiltroReferencia = useRef<HTMLInputElement>();
  const refFiltroSituacao = useRef<HTMLInputElement>();
  const refFiltroAplicabilidade = useRef<HTMLInputElement>();

  // Estado para filtros de colunas (aciona API via useEffect)
  const [filtrosColunas, setFiltrosColunas] = useState<{
    codigoBase?: string;
    cor?: string;
    descricao?: string;
    status?: string;
    referencia?: string;
    situacao?: string;
    aplicabilidade?: string;
  }>({});

  // Estado de exibição dos inputs (atualizado imediatamente, sem debounce)
  const [filtrosColunasDisplay, setFiltrosColunasDisplay] = useState<{
    codigoBase?: string;
    cor?: string;
    descricao?: string;
    status?: string;
    referencia?: string;
    situacao?: string;
    aplicabilidade?: string;
  }>({});

  // Função debounced: atualiza exibição imediatamente, debounce apenas o trigger da API
  const debouncedSetFiltrosColunas = useCallback(
    debounce((coluna: string, valor: string) => {
      setFiltrosColunas((prev) => ({
        ...prev,
        [coluna]: valor,
      }));
      setPage(1);
    }, TIME_DEBOUNCE),
    []
  );

  const debouncedFiltroColuna = useCallback(
    (coluna: string, valor: string) => {
      setFiltrosColunasDisplay((prev) => ({
        ...prev,
        [coluna]: valor,
      }));
      debouncedSetFiltrosColunas(coluna, valor);
    },
    [debouncedSetFiltrosColunas]
  );

  const debouncedFiltroBusca = useCallback(
    debounce((value: string) => {
      setFiltroBusca(value);
      setPage(1);
    }, TIME_DEBOUNCE),
    []
  );

  useEffect(() => {
    carregarFiltros();
    
    // Usar empresa do usuário logado (conforme documentação)
    if (user?.idEmpresa) {
      const idEmpresaParaUsar = empresaId ? parseInt(empresaId) : user.idEmpresa;
      
      // Criar objeto empresa mínimo com o ID do usuário
      setEmpresaSelecionada({
        idEmpresa: idEmpresaParaUsar,
        nomeFantasia: user.empresa || "",
        razaoSocial: user.empresa || "",
        cpfCnpj: "",
        ativo: true,
      } as IEmpresa);
      
      // Atualizar URL se necessário (sem empresaId na URL)
      if (!empresaId) {
        const basePath = isConsulta ? "/aplicabilidades/consulta" : "/aplicabilidades/cadastros";
        navigate(`${basePath}/${idEmpresaParaUsar}`, { replace: true });
      }
    }
  }, [user, empresaId, isConsulta, navigate]);

  useEffect(() => {
    carregarLinhasFiltradas();
    setFiltroLinhaSistema("");
  }, [filtroProjetista]);

  useEffect(() => {
    if (empresaSelecionada) {
      carregarCodigosBase();
      carregarEstado();
      carregarHistoricoImportacao();
    }
    // T-06 (FASE 05): location.key força recarga quando o usuário volta da tela de detalhe
    // (após Agrupar BASE / Agrupar Componente / Remover apontamento, situação muda no backend
    // e o grid deve refletir sem F5 manual).
  }, [empresaSelecionada, page, pageSize, filtroBusca, somenteComAplicabilidade, filtroProjetista, filtroLinhaSistema, filtrosColunas, verDescontinuados, location.key]);


  const carregarFiltros = async () => {
    try {
      const idEmpresa = empresaId ? parseInt(empresaId) : user?.idEmpresa;

      let projetistasParaFiltro: IFSelect[] = [];

      if (idEmpresa) {
        const config = await obterConfiguracaoConsulta(idEmpresa);
        if (config?.projetistasCadastro && config.projetistasCadastro.length > 0) {
          projetistasParaFiltro = config.projetistasCadastro.map(p => ({
            value: p.idProjetista,
            label: p.nome
          }));
        }
        if (config?.referenciasDisponiveis) {
          setReferenciasOpcoes(config.referenciasDisponiveis.map(r => ({
            value: r.idReferencia,
            label: r.descricao
          })));
        }
      }

      // Frente 6 (07/09/2026): sem fallback pelas Referências — o servidor devolve os projetistas
      // liberados para a empresa no Cadastro ou, sem liberação, todos os do cadastro de projetista.

      // FASE 03 (item 10): passar o idEmpresa já calculado evita chamar
      // /Aplicabilidade/Consulta/Configuracao?idEmpresa=0 (que retorna HTTP 400).
      const linhas = idEmpresa ? await listarLinhasSistema(undefined, idEmpresa) : [];
      setProjetistasOpcoes(projetistasParaFiltro);
      setLinhasSistemaOpcoes(linhas);
    } catch (error) {
      console.error("Erro ao carregar filtros:", error);
      openNotification({ message: "Erro ao carregar filtros. Verifique o console para mais detalhes.", type: "error" });
    }
  };

  const carregarLinhasFiltradas = async () => {
    try {
      const idEmpresa = empresaSelecionada?.idEmpresa;
      const linhas = await listarLinhasSistema(
        filtroProjetista ? Number(filtroProjetista) : undefined,
        idEmpresa || undefined
      );
      setLinhasSistemaOpcoes(linhas);
    } catch (error) {
      console.error("Erro ao carregar linhas filtradas:", error);
    }
  };

  // TAREFA 14.3 (FASE 03): mapeamento único do filtro de Situação. O filtro agora é um
  // Select que guarda o código numérico (como string) em filtrosColunas.situacao — sem a
  // ambiguidade de texto livre (ex.: "homo" casava com "Em Homologação" em vez de "Homologada")
  // nem problemas de acento. Usado tanto no grid quanto na exportação.
  const obterSituacaoFiltro = (): number | undefined => {
    const valor = filtrosColunas.situacao;
    if (!valor || valor === "-") return undefined;
    const parsed = parseInt(valor, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  const carregarCodigosBase = async () => {
    if (!empresaSelecionada) {
      console.log("⚠️ carregarCodigosBase: Nenhuma empresa selecionada");
      return;
    }

    console.log("🔍 carregarCodigosBase - Iniciando...");
    console.log("📋 Parâmetros:", {
      idEmpresa: empresaSelecionada.idEmpresa,
      empresaNome: empresaSelecionada.nomeFantasia,
      page,
      limit: pageSize,
      filtroBusca,
      somenteComAplicabilidade,
      idLinhaSistema: filtroLinhaSistema ? Number(filtroLinhaSistema) : undefined,
    });

    setIsLoading(true);
    try {
      // Se houver filtro de situação de aplicabilidade (exceto "-"), buscar apenas componentes
      // que tenham aplicabilidade configurada (caso contrário a coluna Situação fica sem sentido).
      const filtrarSomenteComAplicabilidade = somenteComAplicabilidade ||
        (filtrosColunas.situacao && filtrosColunas.situacao !== "-");

      // TAREFA 14.3 (FASE 03): filtro de situação vem do Select como código numérico.
      const situacaoFiltro = obterSituacaoFiltro();

      const response = await listarCodigosBase({
        idEmpresa: empresaSelecionada.idEmpresa,
        page,
        limit: pageSize,
        filtroBusca: filtroBusca || undefined,
        somenteComAplicabilidade: filtrarSomenteComAplicabilidade || undefined,
        idLinhaSistema: filtroLinhaSistema ? Number(filtroLinhaSistema) : undefined,
        idProjetista: filtroProjetista ? Number(filtroProjetista) : undefined,
        situacao: situacaoFiltro,
        filtroCodigoBase: filtrosColunas.codigoBase || undefined,
        filtroCor: filtrosColunas.cor || undefined,
        filtroDescricao: filtrosColunas.descricao || undefined,
        filtroReferencia: filtrosColunas.referencia || undefined,
        incluirDescontinuados: verDescontinuados,
        // P-18 (2026-06-05): grid do Cadastro sem filtros deve exibir o universo
        // amplo da referência (3.735 para HYDRO), não apenas componentes em
        // tipologias. Revoga parte de P-14 referente ao "sem filtros". Quando
        // Projetista/Linha são preenchidos, o handler continua restringindo
        // por tipologias (ramo idLinhaSistema/idProjetista do handler).
      });

      console.log("✅ Resposta completa da API:", response);
      console.log("📦 response.data:", response.data);
      console.log("📦 response.data.items:", response.data?.items);
      console.log("📊 Total de registros:", response.data?.totalRecords);
      
      // Verificar se a resposta tem a estrutura esperada
      if (response && response.data) {
        const items = response.data.items || [];
        const total = response.data.totalRecords || 0;
        
        console.log(`✓ ${items.length} item(s) recebido(s) de ${total} total`);
        setCodigosBase(items);
        setTotalRecords(total);
      } else {
        console.warn("⚠️ Resposta sem estrutura esperada:", response);
        setCodigosBase([]);
        setTotalRecords(0);
      }
    } catch (error: any) {
      console.error("❌ Erro ao carregar códigos base:", error);
      console.error("❌ Detalhes do erro:", {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
      });
      const errorMessage = error?.response?.data?.errors?.Mensagens?.[0] || 
                          error?.message || 
                          "Erro ao carregar códigos base";
      openNotification({ message: errorMessage, type: "error" });
      setCodigosBase([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
      console.log("🏁 carregarCodigosBase - Finalizado");
    }
  };

  const handleExportar = async () => {
    if (!empresaSelecionada) return;
    setIsLoading(true);
    try {
      const filtrarSomenteComAplicabilidade = somenteComAplicabilidade ||
        (filtrosColunas.situacao && filtrosColunas.situacao !== "-");

      // TAREFA 14.3 (FASE 03): mesmo mapeamento do grid — mantém grid e exportação alinhados.
      const situacaoFiltroExport = obterSituacaoFiltro();

      const blob = await exportarPlanilhaAplicabilidades({
        idEmpresa: empresaSelecionada.idEmpresa,
        filtroBusca: filtroBusca || undefined,
        somenteComAplicabilidade: filtrarSomenteComAplicabilidade || undefined,
        idLinhaSistema: filtroLinhaSistema ? Number(filtroLinhaSistema) : undefined,
        idProjetista: filtroProjetista ? Number(filtroProjetista) : undefined,
        situacao: situacaoFiltroExport,
        filtroCodigoBase: filtrosColunas.codigoBase || undefined,
        filtroCor: filtrosColunas.cor || undefined,
        filtroDescricao: filtrosColunas.descricao || undefined,
        filtroReferencia: filtrosColunas.referencia || undefined,
        // P-18: exportação alinhada ao grid — universo amplo da referência.
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `APLICABILIDADES_${empresaSelecionada.idEmpresa}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      openNotification({ message: "Erro ao exportar planilha.", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickImportar = () => {
    setModalImportacaoAberto(true);
  };

  // TAREFA 06.1 (FASE 03): gera e baixa, no próprio navegador, um .txt com o log completo
  // de erros da importação (sem chamada extra à API). As mensagens já vêm do backend
  // identificando cada Código Base/Aplicável e o motivo.
  const baixarLogErrosImportacao = (erros: string[], nomeArquivo?: string) => {
    const agora = new Date();
    const cabecalho = [
      "Log de erros da importação de aplicabilidades",
      `Data/hora: ${agora.toLocaleString("pt-BR")}`,
      ...(nomeArquivo ? [`Arquivo: ${nomeArquivo}`] : []),
      `Total de erros: ${erros.length}`,
      "",
      "----------------------------------------",
      "",
    ].join("\n");
    const conteudo = cabecalho + erros.map((e, i) => `${i + 1}. ${e}`).join("\n") + "\n";
    const blob = new Blob([conteudo], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `erros_importacao_${agora.getTime()}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // TAREFA 06.1: 1 erro → direto; 2–5 → todos na notificação; >5 → mensagem curta + download
  // automático do log .txt. Mantém a notificação enxuta (sem "mensagem enorme").
  const MAX_ERROS_EXIBIDOS = 5;
  const notificarErrosImportacao = (erros: string[], nomeArquivo?: string) => {
    if (erros.length === 0) return;
    if (erros.length === 1) {
      openNotification({ message: erros[0], type: "error" });
      return;
    }
    if (erros.length <= MAX_ERROS_EXIBIDOS) {
      openNotification({
        message: `Foram encontrados ${erros.length} erros na importação:\n${erros.join("\n")}`,
        type: "error",
      });
      return;
    }
    baixarLogErrosImportacao(erros, nomeArquivo);
    openNotification({
      message: `Foram encontrados ${erros.length} erros na importação. O log foi baixado com os detalhes.`,
      type: "error",
    });
  };

  const handleImportarDoModal = async (file: File) => {
    if (!empresaSelecionada) return;

    setIsLoading(true);
    try {
      const resultado = await importarLoteAplicabilidades(empresaSelecionada.idEmpresa, file);

      // T-5.5 (FASE 03): backend pode devolver HTTP 200 com `resultado.erros` preenchido
      // (validação pré-processamento acumulou erros). Não tratar como sucesso silenciosamente.
      const erros: string[] =
        resultado?.erros ??
        resultado?.Erros ??
        [];

      if (erros.length > 0) {
        notificarErrosImportacao(erros, file?.name);
      } else {
        const totalInclusoes: number = resultado?.totalInclusoes ?? resultado?.TotalInclusoes ?? 0;
        const totalRemocoes: number = resultado?.totalRemocoes ?? resultado?.TotalRemocoes ?? 0;
        // P-16: sempre exibir contagens. Antes, totais=0 escondiam o detalhe e o usuário
        // via "sucesso" mesmo quando nada havia sido alterado (ex.: remoção que caía no
        // no-op silencioso do handler).
        const detalhe = ` (${totalInclusoes} inclusão(ões), ${totalRemocoes} remoção(ões))`;
        const tipo = totalInclusoes + totalRemocoes > 0 ? "success" : "warning";
        const prefixo = totalInclusoes + totalRemocoes > 0
          ? "Importação concluída com sucesso"
          : "Importação processada sem alterações";
        openNotification({ message: `${prefixo}${detalhe}.`, type: tipo });
      }

      await carregarCodigosBase();
      await carregarEstado();
      await carregarHistoricoImportacao();
    } catch (error: any) {
      // O interceptor do axios retorna error.response, então error já é o objeto response
      // Por isso acessamos error.data em vez de error.response.data.
      // TAREFA 06.1 (FASE 03): quando o 400 traz a lista completa de erros de validação,
      // aplicar a mesma regra (1 / 2–5 / >5 com download do log) em vez de exibir só o 1º.
      const errosLista: string[] =
        error?.data?.errors?.Mensagens ??
        error?.data?.Mensagens ??
        (Array.isArray(error?.data?.errors) ? error?.data?.errors : undefined) ??
        [];

      if (Array.isArray(errosLista) && errosLista.length > 0) {
        notificarErrosImportacao(errosLista, file?.name);
      } else {
        const msg = error?.message || "Erro ao importar planilha.";
        openNotification({ message: msg, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // FASE 03 (item 11): handler de modelo CSV removido — somente XLSX.
  const handleBaixarPlanilhaXlsx = async () => {
    try {
      const blob = await baixarPlanilhaModeloXlsx();

      // P-17 item #1: validar que veio binário .xlsx e não JSON de erro com responseType=blob.
      // Backend pode responder 4xx/5xx como blob; sem essa checagem o usuário baixa "arquivo"
      // que o Excel não abre, sem mensagem clara.
      if (!blob || blob.size === 0) {
        throw new Error("Resposta vazia do servidor.");
      }
      const contentType = (blob as any).type as string | undefined;
      if (contentType && contentType.toLowerCase().includes("json")) {
        const texto = await blob.text();
        throw new Error(`Servidor retornou erro: ${texto.substring(0, 300)}`);
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "planilha_padrao_aplicabilidades.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      openNotification({ message: "Planilha XLSX baixada com sucesso.", type: "success" });
    } catch (error: any) {
      const detalhe = error?.message || error?.response?.statusText || "erro desconhecido";
      console.error("[P-17] handleBaixarPlanilhaXlsx falhou:", error);
      openNotification({ message: `Erro ao baixar planilha XLSX: ${detalhe}`, type: "error" });
    }
  };

  const carregarEstado = async () => {
    if (!empresaSelecionada) return;

    try {
      const response = await obterEstado(empresaSelecionada.idEmpresa, {
        filtroBusca: filtroBusca || undefined,
        somenteComAplicabilidade: somenteComAplicabilidade || undefined,
        idLinhaSistema: filtroLinhaSistema ? Number(filtroLinhaSistema) : undefined,
        idProjetista: filtroProjetista ? Number(filtroProjetista) : undefined,
        // P-18: estado computado sobre o universo amplo (alinhado ao grid).
      });
      console.log("📊 Estado carregado:", response.data);
      console.log("📊 temAplicabilidadesEmEdicao:", response.data?.temAplicabilidadesEmEdicao);
      console.log("📊 temAplicabilidadesEmHomologacao:", response.data?.temAplicabilidadesEmHomologacao);
      setEstado(response.data);
    } catch (error) {
      console.error("Erro ao carregar estado:", error);
    }
  };

  const carregarHistoricoImportacao = async () => {
    if (!empresaSelecionada) return;

    console.log(`📋 [carregarHistoricoImportacao] Iniciando para empresa: ${empresaSelecionada.idEmpresa}`);
    try {
      const [ultima, historico] = await Promise.all([
        obterUltimaImportacao(empresaSelecionada.idEmpresa),
        listarHistoricoImportacoes(empresaSelecionada.idEmpresa),
      ]);
      console.log(`📋 [carregarHistoricoImportacao] Última importação:`, ultima);
      console.log(`📋 [carregarHistoricoImportacao] Histórico recebido:`, historico);
      console.log(`📋 [carregarHistoricoImportacao] Total de itens no histórico:`, historico?.length ?? 0);
      setUltimaImportacao(ultima);
      setHistoricoImportacoes(historico);
    } catch (error) {
      console.error("Erro ao carregar histórico de importação:", error);
    }
  };

  const handleRemoverImportacao = async (idHistorico: number) => {
    if (!empresaSelecionada) return;

    setIsLoading(true);
    try {
      await removerImportacao(idHistorico, empresaSelecionada.idEmpresa);
      openNotification({ message: "Importação removida com sucesso", type: "success" });
      await carregarHistoricoImportacao();
      await carregarEstado();
      await carregarCodigosBase();
      setModalImportacaoAberto(false);
    } catch (error) {
      openNotification({ message: "Erro ao remover importação", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadHistorico = async (idHistorico: number) => {
    if (!empresaSelecionada) return;

    setIsLoading(true);
    try {
      await downloadHistoricoImportacao(idHistorico, empresaSelecionada.idEmpresa);
      openNotification({ message: "Download iniciado com sucesso", type: "success" });
    } catch (error: any) {
      const msg =
        error?.response?.data?.Mensagens?.[0] ||
        error?.message ||
        "Erro ao fazer download do arquivo. Este histórico pode não ter o arquivo disponível.";
      openNotification({ message: msg, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAlterarEstado = async (novaSituacao: number) => {
    if (!empresaSelecionada) return;

    setIsLoading(true);
    try {
      await alterarEstado(empresaSelecionada.idEmpresa, novaSituacao);
      openNotification({ message: "Estado alterado com sucesso", type: "success" });
      await carregarEstado();
      await carregarCodigosBase();
    } catch (error) {
      const data = (error as any)?.data ?? (error as any)?.response?.data;
      const msg =
        data?.Mensagens?.[0] ||
        data?.errors?.Mensagens?.[0] ||
        (error as any)?.message ||
        "Erro ao alterar estado";
      openNotification({ message: msg, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // Função para construir URL da imagem do componente
  // O backend já retorna a URL completa através do IGerenciadorArquivoService
  const buildImageSrc = (icone?: string | null): string => {
    if (!icone) return "";
    const trimmed = icone.trim();
    if (!trimmed) return "";
    
    // Se já é uma URL completa (http:// ou https://), retornar como está
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed;
    }
    
    // Se é um caminho relativo que começa com /, adicionar a base URL
    if (trimmed.startsWith("/")) {
      return `${process.env.REACT_APP_API_URL || "http://localhost:3099"}${trimmed}`;
    }
    
    // Se não começa com /, adicionar a base URL
    return `${process.env.REACT_APP_API_URL || "http://localhost:3099"}/${trimmed}`;
  };

  const columns: GridColDef[] = [
    {
      field: "imagem",
      headerName: "",
      width: 80,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const imageUrl = buildImageSrc(params.row.icone);
        return (
          <Box
            sx={{
              width: 50,
              height: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={params.row.codigoBase}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  bgcolor: "#e0e0e0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "codigoBase",
      headerName: "Código",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          inputRef={refFiltroCodigo}
          label="Código"
          name="codigoBase"
          value={filtrosColunasDisplay.codigoBase || ""}
          onChange={(event) => {
            debouncedFiltroColuna("codigoBase", event.target.value);
          }}
        />
      ),
    },
    {
      field: "cor",
      headerName: "Cor",
      width: 120,
      renderHeader: () => (
        <FilterColumn
          inputRef={refFiltroCor}
          label="Cor"
          name="cor"
          value={filtrosColunasDisplay.cor || ""}
          onChange={(event) => {
            debouncedFiltroColuna("cor", event.target.value);
          }}
        />
      ),
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "-"}</Typography>
      ),
    },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 2,
      minWidth: 200,
      renderHeader: () => (
        <FilterColumn
          inputRef={refFiltroDescricao}
          label="Descrição"
          name="descricao"
          value={filtrosColunasDisplay.descricao || ""}
          onChange={(event) => {
            debouncedFiltroColuna("descricao", event.target.value);
          }}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderHeader: () => (
        <Typography variant="caption" sx={{ fontWeight: 500 }}>Status</Typography>
      ),
      renderCell: (params) => {
        const idStatus = params.row.idStatusComponente;
        const label =
          idStatus === 1 ? "Ativo" :
          idStatus === 2 ? "Descontinuado" :
          "—";
        const color: "success" | "warning" | "default" =
          idStatus === 1 ? "success" :
          idStatus === 2 ? "warning" :
          "default";
        return <Chip label={label} color={color} size="small" />;
      },
    },
    {
      field: "referencia",
      headerName: "Referência",
      width: 150,
      renderHeader: () => (
        <FormControl variant="standard" sx={{ minWidth: 100 }}>
          <InputLabel sx={{ fontSize: "0.75rem" }}>Referência</InputLabel>
          <MuiSelect
            value={filtrosColunas.referencia || ""}
            onChange={(e) => {
              const val = e.target.value as string;
              setFiltrosColunas(prev => ({ ...prev, referencia: val }));
              setFiltrosColunasDisplay(prev => ({ ...prev, referencia: val }));
              setPage(1);
            }}
            sx={{ fontSize: "0.8rem" }}
          >
            <MenuItem value="">Todas</MenuItem>
            {referenciasOpcoes.map((ref) => (
              <MenuItem key={ref.value} value={ref.label}>
                {ref.label}
              </MenuItem>
            ))}
          </MuiSelect>
        </FormControl>
      ),
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "MERCADO"}</Typography>
      ),
    },
    {
      field: "situacao",
      headerName: "Situação",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderHeader: () => (
        // TAREFA 14.1 (FASE 03): Select com as 4 situações + "Todas". Guarda o código numérico
        // (como string) em filtrosColunas.situacao, eliminando ambiguidade/acento do texto livre.
        <FormControl variant="standard" sx={{ minWidth: 100 }}>
          <InputLabel sx={{ fontSize: "0.75rem" }}>Situação</InputLabel>
          <MuiSelect
            value={filtrosColunas.situacao ?? ""}
            onChange={(e) => {
              const val = e.target.value as string;
              setFiltrosColunas((prev) => ({ ...prev, situacao: val }));
              setFiltrosColunasDisplay((prev) => ({ ...prev, situacao: val }));
              setPage(1);
            }}
            sx={{ fontSize: "0.8rem" }}
          >
            <MenuItem value="">Todas</MenuItem>
            {SITUACOES.map((s) => (
              <MenuItem key={s.value} value={String(s.value)}>
                {s.label}
              </MenuItem>
            ))}
          </MuiSelect>
        </FormControl>
      ),
      renderCell: (params) => {
        // FASE 03 (item 1.B): mostra a situação quando há QUALQUER vínculo — ativo ou tombstone
        // pendente (possuiSituacao). Assim um código cujo único vínculo foi removido (tombstone)
        // aparece como "Em Edição", mesmo sem aplicabilidade ativa (ícone verde permanece apagado).
        // Fallback para possuiAplicabilidade mantém compatibilidade caso o backend não envie o campo.
        const temSituacao = params.row.possuiSituacao ?? params.row.possuiAplicabilidade;
        if (!temSituacao) {
          return <Typography variant="body2" color="textSecondary">-</Typography>;
        }
        const situacao = SITUACOES.find((s) => s.value === (params.value ?? 0));
        return (
          <Chip
            label={situacao?.label || "Em Edição"}
            size="small"
            color={
              params.value === 3
                ? "success"
                : params.value === 2
                ? "info"
                : params.value === 1
                ? "warning"
                : "default"
            }
          />
        );
      },
    },
    {
      field: "aplicabilidade",
      headerName: "Aplicabilidade",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderHeader: () => (
        <FilterColumn
          inputRef={refFiltroAplicabilidade}
          label="Aplicabilidade"
          name="aplicabilidade"
          value={filtrosColunasDisplay.aplicabilidade || ""}
          onChange={(event) => {
            debouncedFiltroColuna("aplicabilidade", event.target.value);
          }}
        />
      ),
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {params.row.possuiAplicabilidade ? (
            <CheckCircleIcon
              sx={{
                color: "#4caf50",
                fontSize: 24,
              }}
            />
          ) : (
            <CheckCircleIcon
              sx={{
                color: "#9e9e9e",
                fontSize: 24,
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => {
              if (empresaSelecionada) {
                navigate(`/aplicabilidades/cadastros/${empresaSelecionada.idEmpresa}/componente/${params.row.idComponenteBase}`, { state: { codigoBase: params.row } });
              }
            }}
            color="primary"
          >
            <SearchIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <DashboardStackCustom>
      <BackdropLoading isOpen={isLoading} />

      <NavigationHeader
        title={pageTitle}
        label={`Home > ${menuLabel}`}
        hasBackButton={true}
        goBack={() => navigate("/dashboardMof")}
      />

      <Paper elevation={3}>
        {/* Header com Título e Botões */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", bgcolor: "#f5f5f5" }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={8}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Lista de Aplicabilidades de Componentes
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                {/* Botão HOMOLOGAR - move somente Em Edição -> Em Homologação */}
                {/* FASE 05 - T-09: confirmação obrigatória antes da transição. */}
                {estado && estado.temAplicabilidadesEmEdicao && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => abrirConfirmacaoEstado(1)}
                    disabled={isLoading}
                  >
                    HOMOLOGAR
                  </Button>
                )}
                {/* Botão CONCLUIR HOMOLOGAÇÃO - move somente Em Homologação -> Homologada */}
                {/* FASE 05 - T-10: confirmação obrigatória antes da transição. */}
                {estado && estado.temAplicabilidadesEmHomologacao && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => abrirConfirmacaoEstado(2)}
                    disabled={isLoading}
                  >
                    CONCLUIR HOMOLOGAÇÃO
                  </Button>
                )}

                {/* Botão DISPONIBILIZAR MERCADO - apenas MASTER, move somente Homologada -> Disponível Mercado */}
                {/* FASE 05 - T-11: confirmação obrigatória antes da transição. */}
                {isMaster && estado && estado.temAplicabilidadesHomologadas && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => abrirConfirmacaoEstado(3)}
                    disabled={isLoading}
                  >
                    DISPONIBILIZAR MERCADO
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Filtros */}
        <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item>
              <FormControl sx={{ maxWidth: "300px" }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    fontSize: "0.75rem",
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                >
                  Filtro Projetista
                </Typography>
                <MuiSelect
                  value={filtroProjetista}
                  onChange={(e) => {
                    setFiltroProjetista(e.target.value as number | "");
                    setPage(1);
                  }}
                  displayEmpty
                  sx={{
                    bgcolor: "#e0e0e0",
                    borderRadius: "4px",
                    height: "40px",
                    minWidth: "180px",
                    maxWidth: "300px",
                    width: "100%",
                    "& .MuiSelect-select": {
                      py: 1.25,
                      px: 1.5,
                      color: "#212121",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused": {
                      bgcolor: "#e0e0e0",
                    },
                    "&:hover": {
                      bgcolor: "#e0e0e0",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#616161",
                    },
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {projetistasOpcoes.map((projetista) => (
                    <MenuItem key={projetista.value} value={projetista.value}>
                      {projetista.label}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl sx={{ maxWidth: "300px" }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    fontSize: "0.75rem",
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                >
                  Filtro Linha/Sistema
                </Typography>
                <MuiSelect
                  value={filtroLinhaSistema}
                  onChange={(e) => {
                    setFiltroLinhaSistema(e.target.value as number | "");
                    setPage(1);
                  }}
                  displayEmpty
                  sx={{
                    bgcolor: "#e0e0e0",
                    borderRadius: "4px",
                    height: "40px",
                    minWidth: "180px",
                    maxWidth: "300px",
                    width: "100%",
                    "& .MuiSelect-select": {
                      py: 1.25,
                      px: 1.5,
                      color: "#212121",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused": {
                      bgcolor: "#e0e0e0",
                    },
                    "&:hover": {
                      bgcolor: "#e0e0e0",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#616161",
                    },
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {linhasSistemaOpcoes.map((linha) => (
                    <MenuItem key={linha.value} value={linha.value}>
                      {linha.label}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Grid>
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={verDescontinuados}
                    onChange={(e) => {
                      setVerDescontinuados(e.target.checked);
                      setPage(1);
                    }}
                    size="small"
                  />
                }
                label="Ver também códigos descontinuados"
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Botões Importar/Exportar */}
        <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <Stack direction="row" spacing={1}>
                <Tooltip title={!habilitarImportar ? tooltipImportar : ""} arrow disableHoverListener={habilitarImportar}>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleClickImportar}
                      disabled={isLoading || !habilitarImportar}
                      sx={{
                        borderColor: "primary.main",
                        color: "primary.main",
                        "&:hover": {
                          borderColor: "primary.dark",
                          bgcolor: "primary.light",
                          color: "primary.dark",
                        },
                      }}
                    >
                      IMPORTAR
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExportar}
                  disabled={isLoading}
                  sx={{
                    borderColor: "primary.main",
                    color: "primary.main",
                    "&:hover": {
                      borderColor: "primary.dark",
                      bgcolor: "primary.light",
                      color: "primary.dark",
                    },
                  }}
                >
                  EXPORTAR
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ p: 3 }}>
          <StripedDataGrid
            columns={columns}
            rows={codigosBase}
            getRowId={(row) => row.idComponenteBase}
            loading={isLoading}
            page={page - 1}
            pageSize={pageSize}
            rowCount={totalRecords}
            onPageChange={(newPage: number) => setPage(newPage + 1)}
            onPageSizeChange={(newPageSize: number) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            checkboxSelection={false}
          />
        </Box>
      </Paper>

      {/* Modal de Importação */}
      <ModalImportacao
        open={modalImportacaoAberto}
        onClose={() => setModalImportacaoAberto(false)}
        onImportar={handleImportarDoModal}
        onBaixarPlanilhaXlsx={handleBaixarPlanilhaXlsx}
        onRemoverImportacao={handleRemoverImportacao}
        onDownloadHistorico={handleDownloadHistorico}
        ultimaImportacao={ultimaImportacao ? {
          id: ultimaImportacao.id,
          dataHora: new Date(ultimaImportacao.dataImportacao).toLocaleString("pt-BR"),
          situacao: ultimaImportacao.statusDescricao,
          nomeArquivo: ultimaImportacao.nomeArquivo,
          podeRemover: ultimaImportacao.podeRemover, // ✅ TAREFA 2.3: Usar valor calculado no backend
        } : null}
        historicoImportacoes={historicoImportacoes.map((h) => ({
          id: h.id,
          dataHora: new Date(h.dataImportacao).toLocaleString("pt-BR"),
          nomeArquivo: h.nomeArquivo,
        }))}
        situacaoAtual={estado?.situacao ?? 0}
        isLoading={isLoading}
      />

      {/* FASE 05 - T-09/T-10/T-11: confirmação antes de alterar a situação. */}
      <ConfirmarAlteracaoEstadoDialog
        open={confirmacaoEstado.open}
        titulo={confirmacaoEstado.titulo}
        mensagem={confirmacaoEstado.mensagem}
        textoBotaoConfirmar={confirmacaoEstado.textoBotao}
        isLoading={isLoading}
        onConfirmar={confirmarAlteracaoEstado}
        onCancelar={fecharConfirmacaoEstado}
      />

    </DashboardStackCustom>
  );
};
